import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { tasksHaveTimedReminders } from '../../../../../utils/missionReminders';
import { calculateLockStates } from '../../../FlowchartView/utils/dependencyUtils/calculateLockStates';
import { tasksHaveWaitGates } from './conditionWait';

const TICK_MS = 1000;

let revision = 0;
const reactListeners = new Set();
const silentListeners = new Set();
const watchers = new Set();
let intervalId = null;

function tasksNeedClock(tasks) {
  return tasksHaveWaitGates(tasks) || tasksHaveTimedReminders(tasks);
}

/** Ignores the seconds digit so "1m 30s" and "1m 29s" are the same screen. */
function boundarySignature(tasks, now) {
  const locks = calculateLockStates(tasks || [], [], [], now);
  let sig = '';
  for (const id in locks) {
    const state = locks[id];
    if (!state) continue;
    const reason = String(state.lockReason || '').replace(/\d+s\b/g, '');
    sig += `${id}:${state.isLocked ? 1 : 0}:${state.isExcluded ? 1 : 0}:${reason}|`;
  }
  return sig;
}

function ensureInterval() {
  if (intervalId != null || typeof window === 'undefined') return;
  intervalId = window.setInterval(onInterval, TICK_MS);
}

function maybeStopInterval() {
  if (intervalId == null) return;
  if (watchers.size === 0 && silentListeners.size === 0) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function onInterval() {
  const now = Date.now();
  silentListeners.forEach((fn) => {
    try {
      fn(now);
    } catch (err) {
      console.error(err);
    }
  });

  let changed = false;
  const seen = new Map();
  watchers.forEach((watcher) => {
    const tasks = watcher.getTasks();
    if (!tasksNeedClock(tasks)) return;
    let sig = seen.get(tasks);
    if (sig === undefined) {
      sig = boundarySignature(tasks, now);
      seen.set(tasks, sig);
    }
    if (sig !== watcher.lastSig) {
      watcher.lastSig = sig;
      changed = true;
    }
  });
  if (!changed) return;
  revision += 1;
  reactListeners.forEach((fn) => fn());
}

function subscribeReact(onStoreChange) {
  reactListeners.add(onStoreChange);
  return () => {
    reactListeners.delete(onStoreChange);
  };
}

/**
 * Runs every second without re-rendering React. Used for reminders that
 * should fire on time while the campaign screen stays still.
 */
export function subscribeWaitGateTick(listener) {
  silentListeners.add(listener);
  ensureInterval();
  return () => {
    silentListeners.delete(listener);
    maybeStopInterval();
  };
}

const noopSubscribe = () => () => {};

function getSnapshot() {
  return revision;
}

function getServerSnapshot() {
  return 0;
}

/**
 * Live clock for time gates and timed reminders.
 * The screen re-renders when a mission locks or unlocks, not every second.
 * Returns epoch ms, or undefined when nothing is watching so memos stay stable.
 */
export function useWaitGateNow(tasks) {
  const needsTick = useMemo(() => tasksNeedClock(tasks), [tasks]);
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  useEffect(() => {
    if (!needsTick) return undefined;
    const watcher = {
      getTasks: () => tasksRef.current,
      lastSig: boundarySignature(tasksRef.current, Date.now()),
    };
    watchers.add(watcher);
    ensureInterval();
    return () => {
      watchers.delete(watcher);
      maybeStopInterval();
    };
  }, [needsTick]);

  const clockRevision = useSyncExternalStore(
    needsTick ? subscribeReact : noopSubscribe,
    getSnapshot,
    getServerSnapshot,
  );

  if (!needsTick) return undefined;
  return Date.now() + clockRevision * 0;
}
